import React, { useEffect, useState } from 'react';
import '../../assets/styles/form-styles.css';
import '../../assets/styles/persona-registrar.css';
import { Link } from 'react-router-dom';
import { FaChevronRight, FaSave, FaThLarge, FaTimes } from 'react-icons/fa';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import ModulePermissionGrid from '../../components/ModulePermissionGrid';
import { BaseInput, BaseSelect, BaseButton } from '../../components/ui';
import personaService from '../../services/personaService';
import Swal from 'sweetalert2';
import { ROLE_DEFAULT_PERMISSIONS } from '../../config/tenantPermissions';
import { TENANT_ROLES } from '../../config/tenantRoles';

const PersonaRegistrar = () => {
  const [formData, setFormData] = useState({
    dni: '',
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    direccion: '',
    rol: '',
    usuario: '',
    password: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [createAccount, setCreateAccount] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const validateField = (name, value) => {
    switch (name) {
      case 'dni':
        return value && value.length >= 8 ? '' : 'El DNI debe tener al menos 8 caracteres';
      case 'nombre':
        return value ? '' : 'El nombre es requerido';
      case 'apellido':
        return value ? '' : 'El apellido es requerido';
      case 'email':
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? '' : 'El email no es válido';
      case 'telefono':
        return value && value.length >= 9 ? '' : 'El teléfono debe tener al menos 9 dígitos';
      case 'direccion':
        return value ? '' : 'La dirección es requerida';
      case 'rol':
        return value ? '' : 'El rol es requerido';
      case 'usuario':
        return createAccount && !value ? 'El usuario es requerido' : '';
      case 'password':
        return createAccount && (!value || value.length < 6)
          ? 'La contraseña debe tener al menos 6 caracteres'
          : '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) errors[key] = error;
    });
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === 'rol' && value) {
      setSelectedPermissions(ROLE_DEFAULT_PERMISSIONS[value] || []);
    }

    const error = validateField(name, value);
    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const togglePermission = (module) => {
    setSelectedPermissions((prev) =>
      prev.includes(module) ? prev.filter((m) => m !== module) : [...prev, module]
    );
  };

  const resetForm = () => {
    setFormData({
      dni: '',
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      direccion: '',
      rol: '',
      usuario: '',
      password: '',
    });
    setCreateAccount(false);
    setSelectedPermissions([]);
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: 'Campos incompletos',
        text: 'Por favor corrija los errores antes de continuar',
        icon: 'warning',
        confirmButtonText: 'Aceptar',
      });
      return;
    }

    try {
      if (createAccount) {
        await personaService.registrarPersonaYUsuario({
          dni: formData.dni,
          nombre: formData.nombre,
          apellido: formData.apellido,
          email: formData.email,
          telefono: formData.telefono,
          direccion: formData.direccion,
          rol: formData.rol,
          usuario: formData.usuario,
          contraseña: formData.password,
          permisos: selectedPermissions,
        });
        Swal.fire({
          title: '¡Éxito!',
          text: 'La persona y el usuario han sido registrados correctamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        }).then(resetForm);
      } else {
        await personaService.crearPersona({
          dni: formData.dni,
          nombre: formData.nombre,
          apellido: formData.apellido,
          email: formData.email,
          telefono: formData.telefono,
          direccion: formData.direccion,
          rol: formData.rol,
        });
        Swal.fire({
          title: '¡Éxito!',
          text: 'La persona ha sido registrada correctamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
        }).then(resetForm);
      }
    } catch (error) {
      const mensaje = error.response?.data?.mensaje;
      if (mensaje === 'El email ya está registrado') {
        Swal.fire({
          title: 'Email duplicado',
          text: mensaje,
          icon: 'warning',
          confirmButtonText: 'Aceptar',
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: mensaje || 'Error al registrar la persona o usuario',
          icon: 'error',
          confirmButtonText: 'Aceptar',
        });
      }
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Los datos no guardados se perderán',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, limpiar',
      cancelButtonText: 'No, continuar',
    }).then((result) => {
      if (result.isConfirmed) {
        resetForm();
        Swal.fire({
          title: 'Formulario limpiado',
          text: 'Los datos han sido limpiados correctamente',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
        });
      }
    });
  };

  useEffect(() => {
    if (formData.rol) {
      setSelectedPermissions(ROLE_DEFAULT_PERMISSIONS[formData.rol] || []);
    }
  }, [formData.rol]);

  const isValid = (name) => {
    if (formErrors[name]) return false;
    if (name === 'password') return formData.password.length >= 6;
    return Boolean(formData[name]);
  };

  const rolOptions = TENANT_ROLES.map(({ value, label }) => ({ value, label }));

  return (
    <div>
      <Header />
      <Sidebar activeClassName="persona-registrar" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="#">Persona</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right">
                      <FaChevronRight />
                    </i>
                  </li>
                  <li className="breadcrumb-item active">Registrar</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card persona-registrar-card">
                <div className="card-body">
                  <form onSubmit={handleSubmit} noValidate>
                    <div className="form-heading">
                      <h4>Registro de Información de la Persona</h4>
                    </div>

                    <div className="persona-registrar-form-grid">
                      <BaseInput
                        wrapperClassName="persona-registrar-field"
                        name="dni"
                        label="DNI"
                        required
                        value={formData.dni}
                        onChange={handleChange}
                        placeholder="Ingrese el DNI"
                        restrict="number"
                        maxLength={12}
                        error={formErrors.dni}
                        valid={isValid('dni')}
                      />

                      <BaseInput
                        wrapperClassName="persona-registrar-field"
                        name="nombre"
                        label="Nombre"
                        required
                        value={formData.nombre}
                        onChange={handleChange}
                        placeholder="Ingrese el nombre"
                        restrict="text"
                        error={formErrors.nombre}
                        valid={isValid('nombre')}
                      />

                      <BaseInput
                        wrapperClassName="persona-registrar-field"
                        name="apellido"
                        label="Apellidos"
                        required
                        value={formData.apellido}
                        onChange={handleChange}
                        placeholder="Ingrese los apellidos"
                        restrict="text"
                        error={formErrors.apellido}
                        valid={isValid('apellido')}
                      />

                      <BaseInput
                        wrapperClassName="persona-registrar-field"
                        name="email"
                        label="Email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="correo@ejemplo.com"
                        error={formErrors.email}
                        valid={isValid('email')}
                      />

                      <BaseInput
                        wrapperClassName="persona-registrar-field"
                        name="telefono"
                        label="Teléfono"
                        type="tel"
                        required
                        value={formData.telefono}
                        onChange={handleChange}
                        placeholder="Ingrese el teléfono"
                        restrict="number"
                        maxLength={15}
                        error={formErrors.telefono}
                        valid={isValid('telefono')}
                      />

                      <BaseInput
                        wrapperClassName="persona-registrar-field"
                        name="direccion"
                        label="Dirección"
                        required
                        value={formData.direccion}
                        onChange={handleChange}
                        placeholder="Ingrese la dirección"
                        error={formErrors.direccion}
                        valid={isValid('direccion')}
                      />

                      <BaseSelect
                        wrapperClassName="persona-registrar-field"
                        native
                        name="rol"
                        label="Rol"
                        required
                        value={formData.rol}
                        onChange={handleChange}
                        options={rolOptions}
                        placeholder="Seleccione un rol"
                        error={formErrors.rol}
                        valid={isValid('rol')}
                      />

                      <div className="persona-registrar-field persona-registrar-field--checkbox">
                        <span className="persona-registrar-field-label persona-registrar-field-label--spacer">&nbsp;</span>
                        <label className="persona-registrar-account-toggle">
                          <input
                            type="checkbox"
                            checked={createAccount}
                            onChange={() => setCreateAccount(!createAccount)}
                          />
                          <span>Crear cuenta de usuario</span>
                        </label>
                      </div>

                      {createAccount && (
                        <>
                          <hr className="persona-registrar-divider" />

                          <BaseInput
                            wrapperClassName="persona-registrar-field"
                            name="usuario"
                            label="Usuario"
                            required
                            value={formData.usuario}
                            onChange={handleChange}
                            placeholder="Ingrese el nombre de usuario"
                            error={formErrors.usuario}
                            valid={isValid('usuario')}
                          />

                          <BaseInput
                            wrapperClassName="persona-registrar-field"
                            name="password"
                            label="Contraseña"
                            type="password"
                            required
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Mínimo 6 caracteres"
                            helperText="Mínimo 6 caracteres"
                            error={formErrors.password}
                            valid={isValid('password')}
                          />

                          <div className="persona-registrar-section">
                            <div className="persona-registrar-modules-header">
                              <div className="persona-registrar-modules-header-icon">
                                <FaThLarge />
                              </div>
                              <div>
                                <h4>Acceso a módulos</h4>
                                <p>
                                  Al elegir un rol se preseleccionan sus módulos sugeridos.
                                  Puede marcar o desmarcar según necesite.
                                </p>
                              </div>
                            </div>
                            <ModulePermissionGrid
                              selected={selectedPermissions}
                              onToggle={togglePermission}
                            />
                          </div>
                        </>
                      )}

                      <div className="persona-registrar-actions ui-form-actions">
                        <BaseButton
                          type="button"
                          variant="secondary"
                          icon={<FaTimes />}
                          onClick={handleCancel}
                        >
                          Cancelar
                        </BaseButton>
                        <BaseButton
                          type="submit"
                          variant="primary"
                          icon={<FaSave />}
                        >
                          Guardar Información
                        </BaseButton>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonaRegistrar;
